import type { Dispatch, SetStateAction } from "react"
import type { TFunction } from "i18next"
import { Tooltip } from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import type { GridColDef } from "@mui/x-data-grid"

import type { Paged } from "types/common.types"
import type { DateRange } from "types/date.types"
import type { UsersLog, UsersLogsRes } from "types/users.types"

import { formatDateFromMillis, formatFilenameDate } from "helpers/utils/dateUtils"
import { renderDashIfEmpty } from "helpers/utils/tables"
import { formatFilename } from "helpers/utils/common"

export const getAuditTrailTableColumns = (t: TFunction): GridColDef<UsersLog>[] => {
  return [
    { field: "id", headerName: "ID", sortable: false, flex: 1, minWidth: 80 },
    {
      field: "username",
      headerName: t("audit_trail.USERNAME"),
      sortable: false,
      flex: 1,
      minWidth: 160,
    },
    {
      field: "event",
      headerName: t("audit_trail.EVENT"),
      sortable: false,
      flex: 1,
      minWidth: 160,
    },
    {
      field: "reason",
      headerName: t("audit_trail.DESCRIPTION"),
      sortable: false,
      flex: 1,
      minWidth: 200,
      renderCell: renderDashIfEmpty,
    },
    {
      field: "timestamp",
      headerName: t("audit_trail.TIMESTAMP"),
      sortable: false,
      flex: 1,
      minWidth: 150,
      valueFormatter: (value: number) => formatDateFromMillis(value),
    },
    {
      field: "successful",
      // A space, not empty: the CSV header falls back to the field name otherwise.
      headerName: " ",
      sortable: false,
      width: 60,
      // What the CSV writes; the cell shows an icon instead.
      valueFormatter: (value: boolean) =>
        value ? t("audit_trail.EVENT_SUCCESSFUL") : t("audit_trail.EVENT_FAILED"),
      // The span keeps the icon on a text line, which is the row height the old table had.
      renderCell: ({ value }) => (
        <Tooltip
          title={
            value ? t("audit_trail.EVENT_SUCCESSFUL") : t("audit_trail.EVENT_FAILED")
          }
        >
          <span>
            {value ? (
              <CheckCircleOutlineIcon color="success" fontSize="small" />
            ) : (
              <ErrorOutlineIcon color="error" fontSize="small" />
            )}
          </span>
        </Tooltip>
      ),
    },
  ]
}

export const updateTableData = (
  data: Paged<UsersLogsRes> | undefined,
  results: UsersLog[],
  setResults: Dispatch<SetStateAction<UsersLog[]>>,
  setShowSkeleton: Dispatch<SetStateAction<boolean>>,
) => {
  if (data) {
    setResults([...results, ...data.results])
    setShowSkeleton(false)
  }
}

export const getAuditTrailCsvFilename = (
  orgName: string | null,
  dateRange: DateRange | null,
  t: TFunction,
): string => {
  const parts = [
    orgName,
    t("audit_trail.AUDIT_TRAIL"),
    dateRange &&
      `${formatFilenameDate(dateRange.fromDate)}_${t("generic.TO")}_${formatFilenameDate(dateRange.toDate)}`,
  ].filter(Boolean)
  return formatFilename(parts.join("_"), "csv")
}

// The end is reached with no cursor left, or with a page shorter than the one asked for.
export const hasMoreResults = (data: Paged<UsersLogsRes> | undefined, limit: number) =>
  !!data?.next && data.results.length >= limit
